/**
 * Die Tagebuchbeiträge: anlegen, ordnen, und die Überführung des Textes aus der
 * Zeit, in der ein Tag genau ein Textfeld hatte.
 *
 * Bis hierher stand der ganze Tag in DiaryEntry.text. Das ist bei zwei
 * Schreibenden die schlechteste aller Formen: das Dokument geht als Einheit
 * durch den Abgleich, und wer zuletzt tippt, überschreibt den anderen. Jetzt
 * ist jeder Beitrag ein eigenes Dokument (DiaryPost) - zwei Leute, die
 * gleichzeitig schreiben, erzeugen zwei Dokumente, und der Konflikt entsteht
 * gar nicht erst.
 *
 * Auf der laufenden Installation steht in DiaryEntry.text echter Inhalt. Er
 * muss beim ersten Öffnen zu einem Beitrag werden, ohne dass jemand etwas tut
 * und ohne dass etwas verlorengeht. Der Weg dahin ist derselbe wie bei den
 * Checklisten (lib/checks.ts) und den umbenannten Ausgabenkategorien
 * (lib/trip.ts), und zwar beides zusammen:
 *
 *   - Gelesen wird immer beides. beitraege() setzt den alten Text als Beitrag
 *     an die richtige Stelle, ohne dafür etwas zu schreiben. Damit ist er
 *     sichtbar, sobald die App startet - auch offline, auch abgemeldet, und
 *     auch dann noch, wenn Wochen später ein Gerät online geht, das den alten
 *     Text nie losgeworden ist.
 *   - Geschrieben wird einmal. Der Provider legt daraus ein Beitragsdokument an
 *     und räumt DiaryEntry.text leer. Danach findet der nächste Durchgang
 *     nichts mehr - ein Fixpunkt, genau wie bei migrateExpense.
 *
 * Dass die Überführung zweimal ausgeführt keine zwei Beiträge ergibt, hängt an
 * einer einzigen Entscheidung: die Kennung des Beitrags wird aus der Etappen-ID
 * gerechnet und nicht gewürfelt (altbestandId). Zwei Geräte, die gleichzeitig
 * überführen, erzeugen deshalb nicht zwei Beiträge, sondern zweimal dasselbe
 * Dokument - und das ist im Abgleich ein Dokument, kein Duplikat.
 */

import { parseIsoDay } from './calendar'
import { newId } from './id'
import type { DiaryEntry, DiaryPost } from './types'

/** Wer gerade schreibt: Kennung aus dem Token und Anzeigename. */
export interface Verfasser {
  /** Der sub-Claim. Leer heisst: niemand ist angemeldet. */
  id: string
  name: string
}

/** Niemand. Für Aufrufer ohne Anmeldung und als Ausgangswert. */
export const NIEMAND: Verfasser = { id: '', name: '' }

/**
 * Die Kennung des Beitrags, zu dem der Text aus der Zeit vor den Beiträgen
 * wird.
 *
 * Aus der Etappen-ID gerechnet und nicht gewürfelt. Das ist der ganze Schutz
 * gegen die doppelte Überführung: läuft sie zweimal - auf demselben Gerät nach
 * einem Neustart, oder auf zwei Geräten gleichzeitig, die voneinander nichts
 * wissen -, entsteht beide Male dasselbe Dokument. Der Abgleich sieht dann eine
 * Änderung an einem bekannten Dokument und nicht einen zweiten Beitrag mit
 * demselben Text.
 *
 * Das Präfix hält die Kennung von den gewürfelten aus newId('b') fern.
 */
export function altbestandId(stageId: string): string {
  return `alt-${stageId}`
}

/**
 * Der Zeitpunkt, den der überführte Beitrag im Rumpf trägt.
 *
 * Aus dem Datum der Etappe und nicht aus der Uhr: er muss auf jedem Gerät
 * derselbe sein. Zwei Geräte, die denselben alten Text überführen, sollen
 * dasselbe Dokument erzeugen - dieselbe Kennung (altbestandId) und denselben
 * Inhalt. Käme hier die Uhr ins Spiel, unterschieden sich die beiden Fassungen
 * im Rumpf, und der Abgleich hätte einen Konflikt zu entscheiden, wo es gar
 * keinen gibt.
 *
 * Was dieser Zeitpunkt ausdrücklich nicht leistet, ist die Zusage, die hier
 * einmal stand: "ein fester Zeitpunkt aus den Daten selbst kann nicht gewinnen,
 * wo er nicht gewinnen soll". Das gilt nur für Daten aus der Vergangenheit.
 * Diese Reise liegt in der Zukunft - das Etappendatum ist grösser als jede Uhr,
 * die heute läuft. Als Zeitstempel des Dokuments hätte die Überführung damit
 * jeden Konflikt gewonnen, auch den gegen einen Grabstein, und ein gelöschter
 * Abend stände auf allen Geräten wieder da.
 *
 * Über den Abgleich entscheidet deshalb nicht dieser Wert, sondern
 * ALTBESTAND_DOKUMENTZEIT. Hier bleibt, was der Name sagt: die Zeitangabe im
 * Beitrag selbst, für die Reihenfolge im Buch und für die Zeile über dem Text.
 *
 * Ohne Datum bleibt es bei 0. Das ist keine Verlegenheit, sondern die Auskunft:
 * wann dieser Text geschrieben wurde, hat nie jemand festgehalten. Die
 * Oberfläche zeigt dann keine Zeit an.
 *
 * Gelesen wird das Datum über parseIsoDay und nicht mehr unmittelbar über
 * Date.parse. Der Eintrag kommt aus einer Importdatei, die niemand geprüft hat
 * (lib/transfer.ts), und Date.parse beantwortete dort zwei Fragen falsch: den
 * 30. Februar hätte es klaglos in den März gerollt, und "2026-8-3" ohne
 * führende Nullen ist gar kein ISO-Datum mehr - das liest die Laufzeitumgebung
 * nach eigenem Gutdünken als Ortszeit. Damit hinge der Zeitpunkt an der
 * Zeitzone des Geräts, und genau das darf er nicht: zwei Geräte, die denselben
 * alten Text überführen, sollen dasselbe Dokument erzeugen.
 */
export function altbestandZeit(entry: DiaryEntry): number {
  const tag = parseIsoDay(entry.date)
  // Zwanzig Uhr am Abend der Etappe - eine Uhrzeit für die Zeile über dem Text,
  // keine Messung. Das Datum trägt die Aussage.
  return tag === null ? 0 : tag + 20 * 3_600_000
}

/**
 * Der Zeitstempel, mit dem die Überführung als Dokument in den Abgleich geht.
 *
 * Null, und das ist keine Verlegenheit, sondern die ganze Zusage: eine
 * Überführung ist keine Änderung von heute, sondern die Wiedergabe von etwas,
 * das längst dasteht. Sie darf deshalb keinen Konflikt gewinnen, und zwar
 * unabhängig davon, wann sie läuft und was für ein Datum an der Etappe steht.
 * Der Abgleich entscheidet einen Konflikt nach dem Zeitstempel des Dokuments
 * (data/merge.ts), und 0 ist kleiner als jeder Zeitstempel, den ein Server je
 * vergeben hat - die Fassung von drüben gewinnt also immer.
 *
 * Verloren geht dabei nichts. Ein Konflikt entsteht nur, wenn es das Dokument
 * beim Server schon gibt, und dann steht dort entweder dieselbe Überführung,
 * eine bearbeitete Fassung davon oder ein Grabstein - alle drei sind das
 * jüngere Wort. Kennt der Server das Dokument nicht, gibt es keinen Konflikt
 * und der Beitrag wird angelegt.
 *
 * Der naheliegende Gegenvorschlag, min(Etappendatum, jetzt), reicht nicht: ein
 * Gerät, das den alten Text erst nach dem Löschen überführt - offline ist das
 * der Normalfall -, trüge dann "jetzt" und gewänne wieder gegen den Grabstein.
 * Ausserdem nähme er dem Beitrag die Eigenschaft, auf jedem Gerät derselbe zu
 * sein, denn die Uhr stünde im Rumpf.
 */
export const ALTBESTAND_DOKUMENTZEIT = 0

/**
 * Der alte Tagebuchtext als Beitrag, oder nichts.
 *
 * Der Verfasser wird übergeben und hier nicht ermittelt. Das ist der
 * entscheidende Punkt seit der Freigabe: bis dahin hatte eine Reise genau ein
 * Konto, und "wer gerade zusieht" war zwangsläufig auch der, der den Text
 * geschrieben hatte. Sobald geteilt wird, stimmt dieser Satz nicht mehr - der
 * Eingeladene sähe einen alten Text des Besitzers und schriebe ihn beim
 * Überführen sich selbst zu.
 *
 * Wer stattdessen einzusetzen ist, entscheidet altbestandVerfasser in
 * data/trips.ts: der Besitzer der Reise, und nur auf dessen eigenem Gerät.
 * Diese Funktion bleibt dumm und tut, was ihr gesagt wird - so lässt sich beides
 * für sich prüfen.
 */
export function altbestandPost(
  entry: DiaryEntry | undefined,
  ich: Verfasser = NIEMAND,
): DiaryPost | null {
  if (!entry?.text.trim()) return null
  const zeit = altbestandZeit(entry)
  return {
    id: altbestandId(entry.stageId),
    stageId: entry.stageId,
    authorId: ich.id,
    authorName: ich.name,
    createdAt: zeit,
    updatedAt: zeit,
    text: entry.text,
  }
}

/**
 * Ein frischer Beitrag, wie ihn das Schreibfeld anlegt.
 *
 * Die Verfasserkennung geht mit, obwohl der Server sie beim Schreiben ohnehin
 * aus dem Token stempelt und dem Client darin nicht mehr glaubt. Weglassen
 * wäre sauberer, wenn diese App online wäre - ist sie aber nicht: ein Beitrag,
 * der im Funkloch entsteht, muss sofort seinem Verfasser gehören, sonst darf
 * ihn niemand mehr ändern (darfBearbeiten), bis der Server ihn das erste Mal
 * gesehen hat. Der Wert ist derselbe, den der Server einsetzt - er kommt aus
 * demselben Token.
 */
export function neuerPost(
  stageId: string,
  text: string,
  ich: Verfasser,
  now = Date.now(),
): DiaryPost {
  return {
    id: newId('b'),
    stageId,
    authorId: ich.id,
    authorName: ich.name,
    createdAt: now,
    updatedAt: now,
    text,
  }
}

/**
 * Die Beiträge einer Etappe in Leserichtung, samt Altbestand.
 *
 * Ältester zuerst. Ein Tagebuch ist kein Nachrichtenstrom: der Abend wird von
 * oben nach unten gelesen, und im Buch soll die Seite mit dem Anfang des Tages
 * beginnen und nicht mit seinem Ende. Wer neu schreibt, hängt hinten an - das
 * Schreibfeld steht deshalb unter der Liste und nicht darüber.
 *
 * Der Altbestand fällt weg, sobald es das Dokument dazu gibt: dann ist die
 * Überführung gelaufen, und der Beitrag kommt aus dem Bestand statt aus dem
 * alten Feld. Auch ein gelöschter Beitrag zählt dabei als "gibt es" - der
 * Aufrufer reicht gelöschte Dokumente gar nicht erst herein, ihre Kennungen
 * aber schon (bekannt), sonst holte ein alter Text den gelöschten Beitrag
 * wieder zurück.
 *
 * @param posts die Beitragsdokumente dieser Etappe.
 * @param bekannt Kennungen, die es als Dokument gibt, auch als Grabstein.
 */
export function beitraege(
  entry: DiaryEntry | undefined,
  posts: readonly DiaryPost[],
  ich: Verfasser = NIEMAND,
  bekannt: ReadonlySet<string> = new Set(posts.map((p) => p.id)),
): DiaryPost[] {
  const alt = altbestandPost(entry, ich)
  const liste = alt && !bekannt.has(alt.id) ? [alt, ...posts] : [...posts]
  return sortiert(liste)
}

/**
 * Die Beiträge, die in eine Exportdatei gehören: die Dokumente, nicht die
 * Anzeige.
 *
 * Der Unterschied ist der ganze Punkt dieser Funktion. beitraege() setzt den
 * alten Tagebuchtext als Beitrag dazu, damit er sichtbar ist, bevor ihn jemand
 * überführt hat - auf einem fremden Gerät mit leerer Verfasserkennung und der
 * Adresse des Besitzers als Namen (altbestandAnzeige in data/trips.ts). Ginge
 * dieser Beitrag in die Datei, machte der nächste Import ein Dokument daraus,
 * und das gleich doppelt falsch: ein Beitrag ohne Verfasser gehört niemandem
 * und darf laut Serverregel von jedem Schreibenden übernommen werden
 * (internal/api/posts.go), und die saubere Überführung durch den Besitzer wäre
 * ab dann für immer blockiert, weil ueberfuehre die Kennung als bekannt sieht.
 *
 * Der alte Text geht dabei nicht verloren: er steht im Tagebucheintrag, und der
 * ist Teil derselben Datei. Wer sie einliest, sieht ihn beim nächsten Lesen
 * wieder als Beitrag - genau wie auf dem Gerät, von dem die Datei stammt.
 *
 * Entschieden wird das an den Kennungen und nicht daran, welche Liste der
 * Aufrufer hereinreicht. Ein Beitrag kommt nur in die Datei, wenn es ihn als
 * Dokument gibt; wer hier versehentlich die Anzeigeliste übergibt, bekommt
 * trotzdem die Dokumente. Die Zusage soll an dieser Funktion hängen und nicht
 * an der Sorgfalt des Aufrufers - sie hing schon einmal daran.
 *
 * @param nachEtappe Beiträge je Etappe, ohne die gelöschten.
 * @param bekannt die Kennungen aller Beitragsdokumente, auch der gelöschten.
 */
export function beitraegeFuerExport(
  nachEtappe: Readonly<Record<string, readonly DiaryPost[]>>,
  bekannt: ReadonlySet<string>,
): DiaryPost[] {
  return Object.values(nachEtappe)
    .flatMap((liste) => sortiert(liste))
    .filter((post) => bekannt.has(post.id))
}

/**
 * Beiträge in Leserichtung.
 *
 * Bei gleichem Zeitpunkt entscheidet die Kennung. Das ist keine Aussage über
 * die Reihenfolge, sondern die Zusage, dass es überhaupt eine gibt: zwei
 * Beiträge, die in derselben Millisekunde entstanden sind - beim Import einer
 * Datei etwa -, sollen auf jedem Gerät gleich herum stehen.
 */
export function sortiert(posts: readonly DiaryPost[]): DiaryPost[] {
  return [...posts].sort((a, b) =>
    a.createdAt === b.createdAt ? a.id.localeCompare(b.id) : a.createdAt - b.createdAt,
  )
}

/** Was die Überführung eines Tagebucheintrags zu tun hat. */
export interface Überführung {
  /**
   * Der Beitrag, der anzulegen ist. Fehlt, wenn es ihn schon gibt - dann ist
   * die Überführung anderswo schon gelaufen und wird hier nicht wiederholt.
   */
  post?: DiaryPost
  /**
   * Der Tagebucheintrag ohne den alten Text. Fehlt, wenn nichts zu räumen ist.
   */
  entry?: DiaryEntry
  /**
   * Der Zeitstempel, unter dem das Beitragsdokument in den Abgleich geht.
   *
   * Nicht zu verwechseln mit dem Zeitpunkt im Beitrag selbst: der steht im
   * Rumpf, kommt aus dem Datum der Etappe und ist eine Angabe für den Leser
   * (altbestandZeit). Dieser hier ist die Angabe für den Abgleich, und er
   * entscheidet Konflikte. Er steht mit im Auftrag und nicht nur als Konstante
   * daneben, damit die Entscheidung dort liegt, wo die Überführung entschieden
   * wird - und damit sie prüfbar ist, ohne den Provider zu starten.
   *
   * Begründung des Wertes: ALTBESTAND_DOKUMENTZEIT.
   */
  zeit: number
}

/**
 * Was zu tun ist, damit aus dem alten Tagebuchtext ein Beitrag wird.
 *
 * Eine reine Funktion, weil genau hier der Fehler sässe, auf den es ankommt:
 * eine Überführung, die zweimal läuft und zwei Beiträge hinterlässt. Geschrieben
 * wird im Provider, entschieden wird hier - und deshalb lässt sich prüfen, dass
 * das Ergebnis der ersten Runde in der zweiten nichts mehr auslöst.
 *
 * Nichts zu tun heisst: ein leeres Objekt. Das ist der Fixpunkt, den der
 * zweite Durchgang erreicht, und dieselbe Bauart wie bei migrateExpense.
 *
 * @param verfasser wem der überführte Beitrag gehört: dem Besitzer der Reise,
 *   und übergeben nur auf dessen eigenem Gerät (altbestandVerfasser in
 *   data/trips.ts). null heisst "unbekannt oder nicht ich" - dann geschieht
 *   überhaupt nichts, auch das Leerräumen des alten Feldes nicht. Der Text
 *   bleibt sichtbar und wartet auf das richtige Gerät. Ihn hier trotzdem
 *   umzuschreiben hiesse, ihn dem Anwesenden zuzuschlagen, und das steht
 *   danach in jedem gedruckten Buch.
 * @param bekannt die Kennungen aller Beitragsdokumente, auch der gelöschten.
 *   Ein gelöschter Beitrag zählt als vorhanden: wer ihn weggeworfen hat,
 *   bekommt ihn nicht dadurch zurück, dass irgendwo noch ein Gerät mit dem
 *   alten Text steht.
 */
export function ueberfuehre(
  entry: DiaryEntry,
  verfasser: Verfasser | null,
  bekannt: ReadonlySet<string>,
): Überführung {
  // Ohne Verfasser wird nichts festgeschrieben: der Beitrag bekäme entweder
  // keinen Besitzer - und dürfte danach von niemandem mehr geändert werden -
  // oder den falschen. Sichtbar bleibt der alte Text trotzdem, denn beitraege()
  // liest ihn weiter mit.
  const nichts: Überführung = { zeit: ALTBESTAND_DOKUMENTZEIT }
  if (!verfasser?.id) return nichts
  const post = altbestandPost(entry, verfasser)
  if (!post) return nichts
  return {
    ...nichts,
    ...(bekannt.has(post.id) ? {} : { post }),
    entry: { ...entry, text: '' },
  }
}

/**
 * Ob dieser Beitrag von diesem Verfasser geändert oder gelöscht werden darf.
 *
 * Nur der Verfasser. Ein Reisetagebuch ist kein Wiki: was der andere abends
 * geschrieben hat, gehört ihm, auch wenn ein Tippfehler drin steht.
 *
 * Der Server setzt dieselbe Regel durch: er stempelt den Verfasser aus dem
 * geprüften Token und weist eine Änderung am Beitrag eines anderen ab (Status
 * "forbidden" je Änderung, siehe internal/api/posts.go). Auch die Verwaltung
 * einer Reise darf fremde Beiträge nicht anfassen. Diese Prüfung hier ist
 * damit keine Sicherheitszusage mehr, die sie nie war, sondern das, wofür sie
 * gedacht ist: sie verhindert, dass jemand etwas tippt, das gleich darauf
 * verworfen wird.
 *
 * Ein Beitrag ohne Verfasser gehört niemandem und bleibt für alle gesperrt. So
 * sieht ein Beitrag aus einer Datei aus, deren Fassung das Feld noch nicht
 * kannte; ihn dem ersten zuzuschlagen, der ihn ansieht, wäre geraten.
 */
export function darfBearbeiten(post: DiaryPost, ich: Verfasser): boolean {
  return post.authorId !== '' && post.authorId === ich.id
}
